/**
 * Die Zughistorie, aufbereitet — §56, §67 Schritt 47.
 *
 * **Der `payload` ist absichtlich formlos.** `game-events.ts` prüft die Hülle — Typ, Zugnummer,
 * Spieler — und verlangt vom Inhalt nur, JSON-fähig zu sein: was ein `PUSH` oder ein `SCORE`
 * mitführt, hat der Commit-Ablauf entschieden und kann sich ändern. Diese Schicht liest ihn
 * deshalb **vorsichtig**. Ein Zugriff wie `payload.to.q` würde eine Partie, deren Ereignisse aus
 * einer früheren Fassung stammen, beim bloßen **Anzeigen** zum Absturz bringen — und eine
 * Historie, die man nicht mehr öffnen kann, ist keine.
 *
 * Was fehlt, fehlt: `undefined` statt eines erfundenen Ersatzwerts. Bei einem Punktestand ist der
 * Unterschied greifbar — „null Punkte" und „keine Angabe" sind verschiedene Dinge.
 *
 * **Sortiert wird nach `seq`**, der von der Datenbank vergebenen Nummer, nicht nach der
 * Zugnummer: ein Zug kann **mehrere** Ereignisse haben (ein CAPTURE, das zu einem Punkt führt,
 * ist der Normalfall), und die sollen in ihrer Entstehungsreihenfolge stehen.
 *
 * **Die Historie ist öffentlich.** Sie wird nicht projiziert — ein Schlag, ein Punkt, ein
 * Respawn sind Ereignisse, die alle gesehen haben. Previews stehen nicht darin (§41, §57); das
 * setzt schon die Datenbank durch.
 */

import { botFallbackSchema, type BotFallback, type GameEvent } from '@flaghex/contracts';
import type { Hex } from '@flaghex/engine';

export interface HistoryEntry {
  readonly seq: number;
  readonly turnNumber: number;
  /** `null` bei Ereignissen der Partie selbst — `GAME_STARTED`, `GAME_FINISHED`. */
  readonly player: string | null;
  readonly type: GameEvent['type'];
  /**
   * Die Art eines Debug-Eingriffs (**E42**) — `MOVE_PIECE` und was in 11e bis 11g dazukommt.
   *
   * `undefined` bei jedem anderen Ereignis, und auch bei einem Eingriff aus einer Fassung, die
   * das Feld noch nicht kannte: gelesen wird wie alles hier **vorsichtig** (siehe Modulkopf).
   */
  readonly op: string | undefined;
  readonly piece: string | undefined;
  /**
   * Die Figurenklasse, **falls das Ereignis sie mitführt**.
   *
   * Sie steht seit Zyklus 11d im Eingriffsereignis, weil die Historie ohne das Brett lesbar
   * sein muss: in `PAUSED` verdeckt die Projektion alle Figuren (Nr. 26), und dann gibt es
   * nichts, woraus eine Kennung aufzulösen wäre. `undefined` bei allen übrigen Typen — dort
   * löst die Zuordnung der Partie auf, so wie bisher.
   */
  readonly pieceType: string | undefined;
  /**
   * Die bei diesem Eingriff **übergangenen Regeln** (Nr. 38) — leer, wenn keine.
   *
   * Nicht leer heißt: die Stellung ist regelwidrig erzwungen, und das muss man **sehen** („nie
   * stillschweigend"). Gelesen wird vorsichtig wie alles hier: ein Eintrag, der keine
   * Zeichenkette ist, fällt heraus statt die Historie zu sprengen.
   */
  readonly overridden: readonly string[];
  /**
   * Der **betroffene** Spieler eines Eingriffs (11f) — nicht der handelnde.
   *
   * Handelnd ist bei einem Eingriff niemand (`player: null`, Nr. 37); betroffen ist, wessen
   * Punktestand, Uhr oder Zugrecht gesetzt wurde. Zwei Felder, weil es zwei Rollen sind — ein
   * gemeinsames hieße, in der Zeile „Bert hat eingegriffen" zu behaupten.
   */
  readonly subject: string | undefined;
  /**
   * Die Klasse der **geschlagenen** Figur, falls das Ereignis sie mitführt (seit 11g).
   *
   * Dieselbe Begründung wie bei `pieceType`, nur für die andere Seite eines Schlages: eine
   * geschlagene Figur steht nicht mehr auf dem Brett, aus dem die Oberfläche Klassen auflöst.
   */
  readonly victimType: string | undefined;
  /**
   * **Wem die getroffene Figur gehörte** (Befund B6, Zyklus R6).
   *
   * Dieselbe Begründung wie bei `victimType`, einen Schritt weiter: seit **R2** stößt der Stoßer
   * jede Figur, auch die eigene — der Besitz ist damit keine Selbstverständlichkeit mehr. Und er
   * steht im **Ereignis** und nicht im Brett, weil eine geschlagene Figur dort nicht mehr steht.
   */
  readonly victimOwner: string | undefined;
  /** Die gesetzte Restzeit in Millisekunden (11f) — angezeigt wird sie als `m:ss`. */
  readonly remainingMs: number | undefined;
  readonly victim: string | undefined;
  readonly to: Hex | undefined;
  /**
   * **Wo die gestoßene Figur landet** (Befund B1, Zyklus R5).
   *
   * `to` ist bei einem Stoß das Feld, das der Spieler **auswählt** — dort *steht* das Opfer.
   * Das Landefeld ist eine zweite Angabe und bei einem Stoß die wichtigere; ohne sie kann
   * keine Zeile „von … nach …" sagen. Bei einem Stoß über den Rand gibt es keines.
   */
  readonly victimTo: Hex | undefined;
  readonly score: { readonly from: number; readonly to: number } | undefined;
  readonly winner: string | undefined;
  /**
   * **Ein Ende ohne Sieger** — und ein eigenes Feld, weil `winner` es nicht ausdrücken kann.
   *
   * Seit R1 ist der Sieger `TeamId | PlayerId | null` (**E48**), und `null` heißt
   * Unentschieden. Als Zeichenkette gelesen fällt `null` mit „nicht da" zusammen — ein
   * regulärer Ausgang wäre dann von einem alten Ereignis ohne Angabe nicht zu unterscheiden,
   * und beide bekämen denselben Satz. Dieselbe Form wie `offBoard`: eine Frage an den rohen
   * Payload, nicht an seine gelesene Fassung.
   */
  readonly drawn: boolean;
  readonly reason: string | undefined;
  /** Ein Stoß über den Rand (§18) — die Figur ist danach weg, nicht nur verschoben. */
  readonly offBoard: boolean;
  /**
   * Hat ein LLM-Bot diesen Zug als `greedy` gemacht? (`L3b`, **E112** Antwort 2) — gelesen mit
   * dem Schema aus `contracts`; was es nicht besteht, wird verworfen, nicht geraten.
   */
  readonly fallback: BotFallback | undefined;
}

/** Wie oft ein Spieler zurückgefallen ist, und der Grund des letzten Mals — für ⓘ. */
export interface FallbackSummary {
  readonly count: number;
  readonly lastReason: BotFallback['reason'];
}

function stringAt(payload: Record<string, unknown>, key: string): string | undefined {
  const value = payload[key];
  return typeof value === 'string' ? value : undefined;
}

function numberAt(payload: Record<string, unknown>, key: string): number | undefined {
  const value = payload[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function stringsAt(payload: Record<string, unknown>, key: string): readonly string[] {
  const value = payload[key];
  return Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : [];
}

function hexAt(payload: Record<string, unknown>, key: string): Hex | undefined {
  const value = payload[key];
  if (typeof value !== 'object' || value === null) {
    return undefined;
  }
  const candidate = value as { q?: unknown; r?: unknown };
  return typeof candidate.q === 'number' && typeof candidate.r === 'number'
    ? { q: candidate.q, r: candidate.r }
    : undefined;
}

export function historyOf(events: readonly GameEvent[]): readonly HistoryEntry[] {
  return [...events]
    .sort((a, b) => a.seq - b.seq)
    .map((event): HistoryEntry => {
      const payload = event.payload;
      const from = numberAt(payload, 'from');
      const to = numberAt(payload, 'to');
      return {
        seq: event.seq,
        turnNumber: event.turnNumber,
        player: event.player,
        type: event.type,
        op: stringAt(payload, 'op'),
        piece: stringAt(payload, 'piece'),
        pieceType: stringAt(payload, 'pieceType'),
        overridden: stringsAt(payload, 'overridden'),
        subject: stringAt(payload, 'player'),
        remainingMs: numberAt(payload, 'remainingMs'),
        victim: stringAt(payload, 'victim'),
        victimType: stringAt(payload, 'victimType'),
        victimOwner: stringAt(payload, 'victimOwner'),
        to: hexAt(payload, 'to'),
        victimTo: hexAt(payload, 'victimTo'),
        // Nur beim Punkt sind `from` und `to` Zahlen; bei einer Bewegung sind es Felder.
        score: from === undefined || to === undefined ? undefined : { from, to },
        winner: stringAt(payload, 'winner'),
        drawn: payload['winner'] === null,
        reason: stringAt(payload, 'reason'),
        offBoard: payload['capturedByPush'] === true,
        fallback: fallbackAt(payload),
      };
    });
}

function fallbackAt(payload: Record<string, unknown>): BotFallback | undefined {
  const parsed = botFallbackSchema.safeParse(payload['fallback']);
  return parsed.success ? parsed.data : undefined;
}

/**
 * Die Rückfälle je Spieler (`L3b`, **E112** Antwort 2) — aus **derselben** Historie, aus der
 * auch die Zeilen kommen. Der Client hat sie vollständig (`HISTORY` beim Verbinden, danach jedes
 * `EVENT`), also stimmt die Zahl auch nach einem Neuladen.
 */
export function fallbacksOf(entries: readonly HistoryEntry[]): ReadonlyMap<string, FallbackSummary> {
  const result = new Map<string, FallbackSummary>();
  for (const entry of entries) {
    if (entry.fallback === undefined || entry.player === null) continue;
    const before = result.get(entry.player);
    result.set(entry.player, {
      count: (before?.count ?? 0) + 1,
      lastReason: entry.fallback.reason,
    });
  }
  return result;
}
